import React, { useState, useEffect, useRef } from 'react';
import { bookingAPI, reviewAPI } from '../services/api';
import BrandLoader from '../components/BrandLoader';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Collapse,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar
} from '@mui/material';
import jsPDF from 'jspdf';
import {
  Event,
  LocationOn,
  Person,
  Star,
  ArrowBack,
  FilterList,
  Sort,
  Search,
  ExpandMore,
  ExpandLess,
  Chat,
  LocationOn as LocationIcon,
  Store,
  ContentCopy,
  Close,
  Home,
  AccessTime,
  CalendarToday,
  AccountCircle,
  Phone,
  Receipt,
  Info,
  Warning,
  CheckCircle,
  Cancel,
  Schedule,
  Email,
  Payment,
  Error,
  Edit,
  Delete,
  RateReview
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import BottomNav from '../components/BottomNavigation';
import useResponsive from '../hooks/useResponsive';

const Bookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingId: urlBookingId } = useParams(); // Get booking ID from URL
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'past'
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refundMethod, setRefundMethod] = useState('wallet'); // 'wallet' or 'original'
  const [cancelLoading, setCancelLoading] = useState(false);
  
  // Debug logs state
  const [debugLogs, setDebugLogs] = useState([]);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  
  // Review state
  const [bookingReviews, setBookingReviews] = useState({});
  const [reviewLoading, setReviewLoading] = useState(false);
  
  // Delete review confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  
  // Ref to track if URL booking has been loaded
  const urlBookingLoaded = useRef(false);
  
  // State to track if we're loading a specific booking from URL
  const [urlLoading, setUrlLoading] = useState(false);

  // Copy debug logs function
  const copyDebugLogs = async () => {
    const logsText = debugLogs.map(log => {
      return `[${log.type.toUpperCase()}] ${log.message}\n${JSON.stringify(log.data, null, 2)}\n`;
    }).join('\n---\n\n');
    
    const fullDebugText = `BOOKING DEBUG LOGS - ${selectedBooking?.bookingNumber || 'Unknown'}\n` +
      `Timestamp: ${new Date().toISOString()}\n` +
      `Booking Details: ${JSON.stringify(selectedBooking, null, 2)}\n\n` +
      `DEBUG LOGS:\n${logsText}`;
    
    try {
      await navigator.clipboard.writeText(fullDebugText);
      setShowCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy debug logs:', err);
    }
  };

  // Reset modal states when component mounts
  useEffect(() => {
    setShowBookingDetails(false);
    setSelectedBooking(null);
    setShowRescheduleDialog(false);
    setShowCancelDialog(false);
    // Reset URL booking loaded flag when component mounts
    urlBookingLoaded.current = false;
  }, []);

  // Reset URL booking loaded flag when URL changes
  useEffect(() => {
    urlBookingLoaded.current = false;
  }, [urlBookingId]);


  // Ensure we always start with the listing view
  useEffect(() => {
    if (showBookingDetails && !selectedBooking) {
      setShowBookingDetails(false);
    }
  }, [showBookingDetails, selectedBooking]);

  // Listen for navigation event from bottom navigation
  useEffect(() => {
    const handleNavigateToBookingListing = () => {
      setShowBookingDetails(false);
      setSelectedBooking(null);
      setShowRescheduleDialog(false);
      setShowCancelDialog(false);
      // Reset URL booking loaded flag
      urlBookingLoaded.current = false;
      // Navigate to bookings list
      navigate('/bookings', { replace: false });
    };

    window.addEventListener('navigateToBookingListing', handleNavigateToBookingListing);
    
    return () => {
      window.removeEventListener('navigateToBookingListing', handleNavigateToBookingListing);
    };
  }, [navigate]);



  // Mock rescheduling rules (similar to Flutter app)
  const reschedulingRules = [
    { hoursBefore: 24, allowReschedule: true, feePercentage: 0, description: 'Free reschedule up to 24 hours before' },
    { hoursBefore: 12, allowReschedule: true, feePercentage: 10, description: '10% fee for reschedule 12-24 hours before' },
    { hoursBefore: 6, allowReschedule: true, feePercentage: 25, description: '25% fee for reschedule 6-12 hours before' },
    { hoursBefore: 0, allowReschedule: false, feePercentage: 100, description: 'No reschedule allowed within 6 hours' }
  ];

  // Mock cancellation rules (similar to Flutter app)
  const cancellationRules = [
    { hoursBefore: 24, refundAllowed: true, creditPercentage: 100, description: 'Full refund up to 24 hours before' },
    { hoursBefore: 12, refundAllowed: true, creditPercentage: 75, description: '75% refund 12-24 hours before' },
    { hoursBefore: 6, refundAllowed: true, creditPercentage: 50, description: '50% refund 6-12 hours before' },
    { hoursBefore: 0, refundAllowed: false, creditPercentage: 0, description: 'No refund within 6 hours' }
  ];

  // State for bookings data
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle navigation state from transaction clicks
  useEffect(() => {
    if (location.state?.fromTransaction && location.state?.showBookingDetails && bookings.length > 0) {
      
      // If we have a bookingId, find and show that booking
      if (location.state.bookingId) {
        // Find the booking by ID in the current bookings list
        const targetBooking = bookings.find(booking => booking.id === location.state.bookingId);
        if (targetBooking) {
          setSelectedBooking(targetBooking);
          setShowBookingDetails(true);
          // Clear the navigation state after successful navigation
          navigate(location.pathname, { replace: true, state: {} });
        } else {
        }
      }
      // If we have a bookingNumber, find and show that booking
      else if (location.state.bookingNumber) {
        const targetBooking = bookings.find(booking => booking.bookingNumber === location.state.bookingNumber);
        if (targetBooking) {
          setSelectedBooking(targetBooking);
          setShowBookingDetails(true);
          // Clear the navigation state after successful navigation
          navigate(location.pathname, { replace: true, state: {} });
        } else {
        }
      }
    }
  }, [location.state, bookings, navigate, location.pathname]);

  // Handle review success navigation state
  useEffect(() => {
    if (location.state?.reviewSuccess) {
    //  console.log('Review submitted successfully:', location.state.message);
      // Refresh review status after successful review submission
      if (bookings.length > 0) {
        checkReviewStatus(bookings);
      }
      // Clear the navigation state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.reviewSuccess, bookings, navigate, location.pathname]);

  // Fetch bookings from API
  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        status: statusFilter !== 'All' ? statusFilter : undefined,
        sortBy,
        page: 1,
        limit: 50,
        includeItems: true, // Ensure we get complete service items data
        includeServices: true // Ensure service details are included
      };


      const response = await bookingAPI.getBookings(params);
      

      
      if (response.success) {
        // Transform API data to match frontend format
        const transformedBookings = response.data.bookings.map(booking => ({
          id: booking._id,
          bookingNumber: booking.bookingNumber, // Add booking number
          providerId: booking.providerId?._id ? String(booking.providerId._id) : String(booking.providerId),
          serviceName: booking.items?.length > 1 
            ? `${booking.items.length} Services` 
            : (booking.items?.[0]?.serviceId?.serviceName || 'Service'),
          providerName: booking.providerId?.businessName || 'Provider',
          providerImage: booking.providerId?.businessImage || 'https://via.placeholder.com/50x50',
          // Use profilePhotoUrl from S3, fallback to profileImagePath, then businessImage
          providerProfileImage: booking.providerId?.profilePhotoUrl ||
            (booking.providerId?.profileImagePath 
              ? `${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/uploads/${booking.providerId.profileImagePath}`
              : booking.providerId?.businessImage || 'https://via.placeholder.com/100x100'),
          providerPhone: booking.providerId?.phone || '',
          providerAddress: booking.providerId?.address?.fullAddress || 
                          (booking.providerId?.address ? 
                            `${booking.providerId.address.street || ''}, ${booking.providerId.address.city || ''}, ${booking.providerId.address.state || ''} ${booking.providerId.address.postcode || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',') 
                            : '') || '',
          // Payment information
          paymentMethod: booking.paymentMethod || 'card',
          paymentStatus: booking.paymentStatus || 'completed',
          paymentConfirmationId: booking.paymentConfirmationId || '',
          // Provider type for address labels
          providerType: booking.providerId?.businessType || 'salon',
          date: booking.date,
          time: booking.startTime,
          duration: booking.items?.reduce((total, item) => {
            const itemDuration = parseInt(item.serviceId?.duration || item.duration || 30, 10) || 30;
            return total + itemDuration;
          }, 0) || 30,
          // Use pricing.totalPaid instead of totalAmount
          price: booking.pricing?.totalPaid || booking.totalAmount || 0,
          subTotal: booking.pricing?.subtotal || booking.subTotal || 0,
          tax: booking.pricing?.tax || booking.tax || 0,
          total: booking.pricing?.totalPaid || booking.totalAmount || 0,
          status: booking.status,
          location: booking.providerId?.businessName || 'Location',
          salonAddress: booking.providerId?.address?.fullAddress || 
                        (booking.providerId?.address ? 
                          `${booking.providerId.address.street || ''}, ${booking.providerId.address.city || ''}, ${booking.providerId.address.state || ''} ${booking.providerId.address.postcode || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',') 
                          : '') || '',
          rating: booking.providerId?.rating || 0,
          bookingType: booking.bookingType || 'service',
          serviceType: booking.mode || booking.serviceType || 'in_salon',
          serviceAddress: booking.serviceAddress,
          providerType: booking.providerId?.businessType || 'salon',
          additionalNotes: booking.specialInstructions || '',
          hoursUntilBooking: calculateHoursUntilBooking(booking.date, booking.startTime),
          services: booking.items?.map(item => ({
            id: item.serviceId?._id || item.serviceId || `service-${Math.random()}`,
            name: item.serviceId?.serviceName || item.name || 'Service',
            price: item.price || 0,
            duration: parseInt(item.serviceId?.duration || item.duration || 30, 10) || 30,
            image: item.serviceId?.imagePaths?.[0] || 'https://via.placeholder.com/80x80'
          })) || [],
          // Keep the original items array like ServiceProviderApp does
          items: booking.items || [],
          // Add reschedule-related fields
          reschedulePayments: booking.reschedulePayments || [],
          repaymentFee: booking.repaymentFee || 0,
          lastRescheduledAt: booking.lastRescheduledAt,
          // Add cancellation-related fields
          cancellationDate: booking.cancellationDate,
          cancellationReason: booking.cancellationReason,
          cancellationDetails: booking.cancellationDetails,
          refundAmount: booking.refundAmount,
          refundMethod: booking.refundMethod,
          cancellationFee: booking.cancellationFee,
          appliedCancelRule: booking.appliedCancelRule
        }));
        
        
        setBookings(transformedBookings);
        
        // Check review status for completed bookings
        checkReviewStatus(transformedBookings);
      } else {
        setError('Failed to load bookings');
      }
    } catch (error) {
      
      
      setError(`Failed to load bookings: ${error.message}`);
      // Fallback to mock data if API fails
      setBookings([
        {
          id: 'BK2024001',
          providerId: 'salon-1',
          serviceName: 'Hair Cut',
          providerName: 'Beauty Salon Pro',
          providerImage: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=50&h=50&fit=crop',
          providerProfileImage: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop',
          providerPhone: '+1 (555) 123-4567',
          providerAddress: '123 Main Street, Downtown, City',
          date: '2024-02-15',
          time: '14:00',
          duration: '30 min',
          price: 45,
          subTotal: 45,
          tax: 3.6,
          total: 48.6,
          status: 'confirmed',
          location: 'Beauty Salon Pro',
          salonAddress: '123 Main Street, Downtown, City',
          rating: 4.8,
          bookingType: 'service',
          serviceType: 'in_salon',
          serviceAddress: null,
          providerType: 'salon',
          additionalNotes: 'Please arrive 10 minutes early',
          hoursUntilBooking: 18,
          services: [
            {
              id: 'service-1',
              name: 'Hair Cut',
              price: 45,
              duration: 30,
              image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=80&h=80&fit=crop'
            }
          ]
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Check review status for all completed bookings
  const checkReviewStatus = async (bookingsList) => {
    try {
      const completedBookings = bookingsList.filter(b => b.status === 'completed');
      if (completedBookings.length === 0) return;
      
      
      const response = await reviewAPI.getMyReviews();
      const reviews = response.data.data?.reviews || [];
      
      
      const reviewsMap = {};
      completedBookings.forEach(booking => {
        const review = reviews.find(r => {
          // Handle multiple formats of bookingId
          const reviewBookingId = r.bookingId?._id || r.bookingId;
          const bookingIdStr = String(booking.id);
          const reviewBookingIdStr = String(reviewBookingId);
          const bookingMatches = reviewBookingIdStr === bookingIdStr;
          
          return bookingMatches;
        });
        reviewsMap[booking.id] = review || null;
      });
      
      setBookingReviews(reviewsMap);
    } catch (error) {
      console.error('Error checking review status:', error);
    }
  };

  // Handle leave review
  const handleLeaveReview = (booking) => {
    navigate('/leave-review', { 
      state: { 
        booking: {
          ...booking,
          _id: booking.id, // Use the booking.id as _id for backend
          id: booking.id, // Keep original id
          bookingId: booking.id, // Explicit bookingId field
          providerId: booking.providerId,
          providerName: booking.providerName,
          providerLogo: booking.providerProfileImage,
          serviceName: booking.serviceName,
          bookingNumber: booking.bookingNumber
        } 
      } 
    });
  };

  // Handle edit review
  const handleEditReview = (booking) => {
    const bookingToUse = booking || selectedBooking;
    const bookingKey = bookingToUse?.id || bookingToUse?._id;
    const existingReview = bookingReviews[bookingKey];
    
    if (existingReview) {
      navigate('/leave-review', { 
        state: { 
          booking: {
            ...bookingToUse,
            _id: bookingKey,
            id: bookingKey,
            bookingId: bookingKey,
            providerId: bookingToUse.providerId,
            providerName: bookingToUse.providerName,
            providerLogo: bookingToUse.providerProfileImage,
            serviceName: bookingToUse.serviceName,
            bookingNumber: bookingToUse.bookingNumber
          },
          existingReview
        } 
      });
    }
  };

  // Handle delete review - show confirmation dialog
  const handleDeleteReview = (booking) => {
    const bookingToUse = booking || selectedBooking;
    const bookingKey = bookingToUse?.id || bookingToUse?._id;
    const existingReview = bookingReviews[bookingKey];
    
    if (!existingReview) return;

    setReviewToDelete({ booking: bookingToUse, review: existingReview });
    setShowDeleteDialog(true);
  };

  // Confirm delete review
  const confirmDeleteReview = async () => {
    if (!reviewToDelete) return;

    try {
      setReviewLoading(true);
      await reviewAPI.deleteReview(reviewToDelete.review._id);
      
      // Update local state - use the correct booking key
      const bookingKey = reviewToDelete.booking.id || reviewToDelete.booking._id;
      setBookingReviews(prev => ({
        ...prev,
        [bookingKey]: null
      }));
      
      // Close delete dialog first
      setShowDeleteDialog(false);
      setReviewToDelete(null);
      
      // Show success message
      setSuccessMessage('Review deleted successfully');
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error deleting review:', error);
      alert(error.message || 'Failed to delete review');
    } finally {
      setReviewLoading(false);
    }
  };

  // Calculate hours until booking
  const calculateHoursUntilBooking = (date, time) => {
    try {
      let bookingDateTime;
      
      // Handle different date formats
      if (date instanceof Date) {
        // If date is already a Date object
        bookingDateTime = new Date(date);
      } else if (typeof date === 'string') {
        if (date.includes('T')) {
          // ISO format: "2025-09-10T10:30:00.000Z" or "2025-09-10T00:00:00.000Z"
          const isoDate = new Date(date);
          
          // If the time is 00:00:00 (midnight), it means we need to use the separate time parameter
          if (time && (isoDate.getUTCHours() === 0 && isoDate.getUTCMinutes() === 0)) {
            // Extract date part and combine with time in local timezone
            const dateOnly = date.split('T')[0]; // "2025-09-10"
            const [hours, minutes] = time.split(':');
            bookingDateTime = new Date(parseInt(dateOnly.split('-')[0]), parseInt(dateOnly.split('-')[1]) - 1, parseInt(dateOnly.split('-')[2]), parseInt(hours), parseInt(minutes), 0);
          } else {
            // Use the ISO date as-is
            bookingDateTime = isoDate;
          }
        } else if (time) {
          // Separate date and time: "2025-09-10" and "10:30" or "16:30"
          // Create a proper datetime string in local timezone
          const dateStr = date.includes('-') ? date : date.split('/').reverse().join('-');
          const timeStr = time.includes(':') ? time : `${time.slice(0, 2)}:${time.slice(2)}`;
          
          // Create date in local timezone to avoid UTC conversion issues
          const [year, month, day] = dateStr.split('-');
          const [hours, minutes] = timeStr.split(':');
          bookingDateTime = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes), 0);
        } else {
          // Just date: "2025-09-10"
          bookingDateTime = new Date(date);
        }
      } else {
        // Fallback
        bookingDateTime = new Date(date);
      }
      
      // Ensure we have a valid date
      if (isNaN(bookingDateTime.getTime())) {
        return 0;
      }
      
    const now = new Date();
    const diffMs = bookingDateTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);
      
      
      // Return the calculated hours (can be negative if booking is in the past)
    return Math.round(diffHours);
    } catch (error) {
      return 0; // Default to 0 hours if calculation fails
    }
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime, duration) => {
    try {
      if (!startTime || !duration) return null;
      
      const [hours, minutes] = startTime.split(':').map(Number);
      const startMinutes = hours * 60 + minutes;
      const endMinutes = startMinutes + duration;
      
      const endHours = Math.floor(endMinutes / 60);
      const endMins = endMinutes % 60;
      
      return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
    } catch (error) {
      return null;
    }
  };

  // Fetch bookings when component mounts or filters change
  useEffect(() => {
    fetchBookings();
  }, [statusFilter, sortBy]);

  // Handle URL parameter to open booking details directly
  useEffect(() => {
    if (urlBookingId && !urlBookingLoaded.current) {
      urlBookingLoaded.current = true;
      setUrlLoading(true);
    }
  }, [urlBookingId]);

  // Wait for bookings to load and then show the specific booking
  useEffect(() => {
    if (urlBookingId && urlLoading && !loading && bookings.length > 0) {
      const booking = bookings.find(b => b.id === urlBookingId || b._id === urlBookingId);
      
      if (booking) {
        // Always show the booking once we find it, regardless of services completeness
        setSelectedBooking(booking);
        setShowBookingDetails(true);
        setUrlLoading(false);
      } else {
        // No booking found, stop loading
        setUrlLoading(false);
      }
    }
  }, [urlBookingId, urlLoading, loading, bookings]);

  // Fetch bookings on component mount
  useEffect(() => {
    fetchBookings();
  }, [statusFilter, sortBy]);

  const statusOptions = [
    { name: 'All', color: '#006AF9' },
    { name: 'Confirmed', color: '#006AF9' },
    { name: 'En Route', color: '#006AF9' },
    { name: 'In Progress', color: '#006AF9' },
    { name: 'Completed', color: '#2E7D32' },
    { name: 'Cancelled', color: '#d32f2f' },
    { name: 'No Show', color: '#ff9800' },
    { name: 'Rescheduled', color: '#9c27b0' },
    { name: 'Refunded', color: '#4caf50' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return '#006AF9';
      case 'en_route':
        return '#006AF9';
      case 'in_progress':
        return '#006AF9';
      case 'completed':
        return '#2E7D32'; // Green for completed
      case 'cancelled':
        return '#d32f2f';
      case 'no_show':
        return '#ff9800';
      case 'rescheduled':
        return '#9c27b0';
      case 'refunded':
        return '#4caf50';
      default:
        return '#006AF9';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'en_route':
        return 'En Route';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No Show';
      case 'rescheduled':
        return 'Rescheduled';
      case 'refunded':
        return 'Refunded';
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    // Return null to remove icons from status chips
    return null;
  };

  // Check if booking is ongoing (not completed, cancelled, refunded, no show)
  const isOngoingBooking = (status) => {
    return !['completed', 'cancelled', 'refunded', 'no_show'].includes(status);
  };

  // Check if booking date is today or in the future
  const isBookingTodayOrFuture = (bookingDate) => {
    if (!bookingDate) return false;
    
    const today = new Date();
    const bookingDateObj = new Date(bookingDate);
    
    // Reset time to start of day for accurate date comparison
    today.setHours(0, 0, 0, 0);
    bookingDateObj.setHours(0, 0, 0, 0);
    
    return bookingDateObj >= today;
  };

  // Check if booking should show reschedule/cancel buttons
  const shouldShowActionButtons = (booking) => {
    // Only show for confirmed bookings that are today or in the future and not completed
    // Also check if booking has not been rescheduled (to hide entire action button section)
    const shouldShow = booking.status === 'confirmed' && 
           isBookingTodayOrFuture(booking.date) && 
           booking.status !== 'completed' &&
           (!booking.reschedulePayments || booking.reschedulePayments.length === 0);
    
    
    return shouldShow;
  };

  const handleBookingClick = async (booking) => {
    // Clear previous debug logs when selecting a new booking
    setDebugLogs([]);
    
    try {
      // Fetch fresh booking details to get latest data including coupon fields
      const response = await bookingAPI.getBookingDetails(booking.id);
      
      if (response.success) {
        // Transform the services data from items array
        const services = response.data.items?.map(item => ({
          id: item.serviceId?._id,
          name: item.serviceId?.serviceName,
          price: item.price,
          duration: parseInt(item.serviceId?.duration || item.duration || 30, 10) || 30,
          image: item.serviceId?.imagePaths?.[0] || 'https://via.placeholder.com/80x80'
        })).filter(service => service.id && service.name) || [];
        
        // Ensure the response data has an 'id' property for consistency
        const bookingData = {
          ...response.data,
          id: response.data.id || response.data._id || booking.id,
          services: services,
          serviceName: services.length > 1 
            ? `${services.length} Services` 
            : (services[0]?.name || response.data.serviceName || 'Service')
        };
        
        setSelectedBooking(bookingData);
      } else {
        // Fallback to using the booking from the list
        setSelectedBooking(booking);
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      // Fallback to using the booking from the list
      setSelectedBooking(booking);
    }
    setShowBookingDetails(true);
    
    // Update URL to include booking ID for shareable link
    navigate(`/bookings/${booking.id}`, { replace: false });
  };

  // Alias for URL parameter handling
  const handleViewBooking = handleBookingClick;

  const handleReschedule = (booking) => {
    setSelectedBooking(booking);
    setShowRescheduleDialog(true);
  };

  const handleCancel = (booking) => {
    setSelectedBooking(booking);
    setShowCancelDialog(true);
  };

  const handleBack = () => {
    if (showBookingDetails) {
      setShowBookingDetails(false);
      setSelectedBooking(null);
      // Navigate back to bookings list
      navigate('/bookings', { replace: false });
    } else {
      navigate('/home');
    }
  };

  const handleChat = (booking) => {
    // TODO: Implement chat functionality
  };

  const handleLocation = (booking) => {
    // TODO: Implement location tracking
  };

  const handleProviderProfile = (booking) => {
    // TODO: Navigate to provider profile
  };


  const getApplicableRescheduleRule = (booking) => {
    if (!booking) return null;
    
    // Recalculate hours to ensure accuracy
    const hoursUntilBooking = calculateHoursUntilBooking(booking.date, booking.time);
    
    for (let rule of reschedulingRules) {
      if (hoursUntilBooking > rule.hoursBefore) {
        return rule;
      }
    }
    return reschedulingRules[reschedulingRules.length - 1];
  };

  const getApplicableCancelRule = (booking) => {
    if (!booking) {
      return null;
    }
    
    // Recalculate hours to ensure accuracy
    const hoursUntilBooking = calculateHoursUntilBooking(booking.date, booking.time);
    
    for (let rule of cancellationRules) {
      if (hoursUntilBooking > rule.hoursBefore) {
        return rule;
      }
    }
    return cancellationRules[cancellationRules.length - 1];
  };

  const calculateRescheduleFee = (booking) => {
    const rule = getApplicableRescheduleRule(booking);
    if (!rule || !rule.allowReschedule) return 0;
    
    // Calculate actual amount paid (after coupons, wallet, gems discounts)
    const actualAmountPaid = booking.total || booking.totalAmount || booking.price || booking.subTotal;
    
    return (rule.feePercentage / 100) * actualAmountPaid;
  };

  const calculateCancelRefund = (booking) => {
    const rule = getApplicableCancelRule(booking);
    
    if (!rule || !rule.refundAllowed) {
      return 0;
    }
    
    // Calculate actual amount paid (after coupons, wallet, gems discounts)
    const actualAmountPaid = booking.total || booking.totalAmount || booking.price || booking.subTotal;
    
    const refundAmount = (rule.creditPercentage / 100) * actualAmountPaid;
    return refundAmount;
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking || cancelLoading) return;
    
    setCancelLoading(true);
    try {
      const cancelRule = getApplicableCancelRule(selectedBooking);
      const refundAmount = calculateCancelRefund(selectedBooking);
      
      const cancelData = {
        reason: 'Customer requested cancellation',
        refundMethod: refundMethod,
        refundAmount: refundAmount,
        cancelRule: cancelRule
      };

      const bookingId = selectedBooking._id || selectedBooking.id;
      
      if (!bookingId) {
        throw new Error('Booking ID is missing');
      }
      
      const response = await bookingAPI.cancelBooking(bookingId, cancelData);
      
      
      if (response.success) {
        // Refresh bookings list first to get updated data from server
        await fetchBookings();
        
        // Find the updated booking in the refreshed list and update selectedBooking
        const currentBookingId = selectedBooking._id || selectedBooking.id;
        const updatedBooking = bookings.find(b => (b._id || b.id) === currentBookingId || b.bookingNumber === selectedBooking.bookingNumber);
        
        if (updatedBooking) {
          setSelectedBooking(updatedBooking);
        } else {
          // Fallback: Update the selected booking with cancellation details from response
          if (response.data && response.data.cancellationDetails) {
            setSelectedBooking(prev => ({
              ...prev,
              status: 'cancelled',
              cancellationDate: response.data.cancellationDetails.cancellationDate,
              cancellationReason: response.data.cancellationDetails.reason,
              refundAmount: response.data.refundAmount,
              cancellationFee: response.data.cancellationFee,
              appliedCancelRule: response.data.cancellationDetails.appliedCancelRule,
              cancellationDetails: {
                amountTransferred: response.data.refundAmount,
                amountDeducted: response.data.cancellationFee,
                transferMethod: response.data.cancellationDetails.refundMethod,
                transferReference: response.data.transferReference,
                transferDate: response.data.cancellationDetails.cancellationDate
              }
            }));
          }
        }
        
        setShowCancelDialog(false);
        setRefundMethod('wallet'); // Reset to default
        // Show success dialog
        setSuccessMessage('Booking cancelled successfully');
        setShowSuccessDialog(true);
      } else {
        setSuccessMessage('Failed to cancel booking: ' + response.error);
        setShowSuccessDialog(true);
      }
    } catch (error) {
      setSuccessMessage('Failed to cancel booking. Please try again.');
      setShowSuccessDialog(true);
    } finally {
      setCancelLoading(false);
    }
  };

  // Invoice Download Function
  const handleDownloadInvoice = (booking) => {
    const doc = new jsPDF();
    
    // Set font
    doc.setFont('helvetica');
    
    // Helper function to add footer to current page
    const addFooter = (doc, pageNum) => {
      const footerY = doc.internal.pageSize.height - 15;
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Thank you for choosing Nistylez!', 105, footerY - 10, { align: 'center' });
      doc.text('For any queries, please contact our support team.', 105, footerY - 5, { align: 'center' });
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, footerY, { align: 'center' });
    };

    // Helper function to check if we need a new page
    const checkPageBreak = (requiredSpace) => {
      if (yPos + requiredSpace > doc.internal.pageSize.height - 40) {
        addFooter(doc, doc.getNumberOfPages());
        doc.addPage();
        yPos = 20; // Reset position for new page
        return true;
      }
      return false;
    };
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(30, 58, 138); // Dark blue
    doc.text('NISTYLEZ', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Welcome to the New Era of Beauty & Wellness', 105, 30, { align: 'center' });
    
    // Invoice Title
    doc.setFontSize(14);
    doc.setTextColor(30, 58, 138);
    doc.text('BOOKING INVOICE', 105, 40, { align: 'center' });
    
    // Booking Details - More compact
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    
    let yPos = 50;
    const pageHeight = doc.internal.pageSize.height;
    
    // Booking ID/Number - Compact
    doc.setFontSize(11);
    doc.setTextColor(30, 58, 138);
    doc.text(`Booking ${booking.bookingNumber ? `#${booking.bookingNumber}` : `ID: ${booking.id}`}`, 20, yPos);
    yPos += 8;
    
    // Date and Time - Compact
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text(`Date: ${new Date(booking.date).toLocaleDateString()}`, 20, yPos);
    doc.text(`Time: ${booking.time}`, 120, yPos);
    yPos += 8;
    
    // Provider Details - Compact
    doc.text(`Provider: ${booking.providerName}`, 20, yPos);
    yPos += 6;
    
    if (booking.providerPhone) {
      doc.text(`Phone: ${booking.providerPhone}`, 20, yPos);
      yPos += 6;
    }
    
    // Service Details - More compact with proper page breaks
    checkPageBreak(20);
    if (booking.services && booking.services.length > 1) {
      doc.text(`Services: ${booking.services.length} Services`, 20, yPos);
      yPos += 8;
      booking.services.forEach((service, index) => {
        checkPageBreak(15);
        doc.text(`• ${service.name}`, 25, yPos);
        doc.text(`$${service.price}`, 150, yPos);
        yPos += 6;
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`Duration: ${service.duration} min`, 25, yPos);
        doc.setFontSize(9);
        doc.setTextColor(50, 50, 50);
        yPos += 6;
      });
    } else {
      doc.text(`Service: ${booking.serviceName}`, 20, yPos);
      yPos += 8;
      doc.text(`Duration: ${booking.duration} minutes`, 20, yPos);
      yPos += 8;
    }
    
    // Service Type and Address - Compact
    checkPageBreak(20);
    if (booking.serviceType === 'at_home') {
      doc.text(`Service Type: At Home`, 20, yPos);
      yPos += 8;
      doc.text(`Service Address: ${booking.serviceAddress}`, 20, yPos);
      yPos += 10;
    } else {
      doc.text(`Service Type: In ${booking.providerType === 'salon' ? 'Salon' : 'Location'}`, 20, yPos);
      yPos += 8;
      doc.text(`${booking.providerType === 'salon' ? 'Salon' : 'Service'} Address: ${booking.providerAddress}`, 20, yPos);
      yPos += 10;
    }
    
    // Payment Information - Compact
    checkPageBreak(30);
    doc.setFontSize(11);
    doc.setTextColor(30, 58, 138);
    doc.text('Payment Information', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text(`Payment Method: ${booking.paymentMethod === 'card' ? 'Credit Card' : booking.paymentMethod === 'wallet' ? 'Wallet' : booking.paymentMethod === 'cash' ? 'Cash' : 'Credit Card'}`, 20, yPos);
    yPos += 8;
    
    if (booking.stripePaymentIntentId || booking.paymentConfirmationId || booking.paymentConfirmationNumber) {
      doc.text(`Transaction ID: ${booking.stripePaymentIntentId || booking.paymentConfirmationId || booking.paymentConfirmationNumber}`, 20, yPos);
      yPos += 8;
    }
    
    doc.text(`Payment Status: ${booking.paymentStatus?.toUpperCase() || 'PAID'}`, 20, yPos);
    yPos += 10;
    
    // Bill Summary - Compact
    checkPageBreak(40);
    doc.setFontSize(11);
    doc.setTextColor(30, 58, 138);
    doc.text('Bill Summary', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    
    // Sub Total - Compact
    doc.text('Sub Total:', 20, yPos);
    doc.text(`$${(booking.subTotal || booking.total || booking.totalAmount || booking.price || 0).toFixed(2)}`, 150, yPos);
    yPos += 8;
    
    // Coupon Discount - Compact
    if (booking.couponDiscount > 0) {
      doc.setTextColor(34, 139, 34); // Green color for discount
      doc.text(`Coupon Discount (${booking.appliedCoupon || 'Applied'}):`, 20, yPos);
      doc.text(`-$${booking.couponDiscount.toFixed(2)}`, 150, yPos);
      yPos += 8;
      doc.setTextColor(50, 50, 50);
    }
    
    // Wallet Applied - Compact
    if (booking.walletBalanceUsed > 0 || booking.appliedWalletBalance > 0) {
      doc.setTextColor(34, 139, 34); // Green color for discount
      doc.text('Wallet Applied:', 20, yPos);
      doc.text(`-$${(booking.walletBalanceUsed || booking.appliedWalletBalance || 0).toFixed(2)}`, 150, yPos);
      yPos += 8;
      doc.setTextColor(50, 50, 50);
    }
    
    // Gems Applied - Compact
    if (booking.gemsUsed > 0 || booking.appliedGems > 0) {
      const gemsUsed = booking.gemsUsed || booking.appliedGems || 0;
      doc.setTextColor(34, 139, 34); // Green color for discount
      doc.text(`${gemsUsed} gems used -$${((gemsUsed / 1000) * 10).toFixed(2)}`, 20, yPos);
      yPos += 8;
      doc.setTextColor(50, 50, 50);
    }
    
    // Tax - Compact
    if (booking.tax) {
      doc.text('Tax:', 20, yPos);
      doc.text(`$${booking.tax.toFixed(2)}`, 150, yPos);
      yPos += 8;
    }
    
    // Total - Compact
    checkPageBreak(15);
    doc.setFontSize(11);
    doc.setTextColor(30, 58, 138);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Paid:', 20, yPos + 5);
    doc.text(`$${(booking.total || booking.totalAmount || booking.price || 0).toFixed(2)}`, 150, yPos + 5);
    yPos += 15;
    
    // Original Appointment Details (for rescheduled bookings)
    if (booking.originalBookingDetails) {
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(12);
      doc.setTextColor(255, 140, 0); // Orange color
      doc.text('Original Appointment Details', 20, yPos);
      yPos += 15;
      
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text(`Original Date: ${new Date(booking.originalBookingDetails.date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}`, 20, yPos);
      yPos += 10;
      
      doc.text(`Original Time: ${booking.originalBookingDetails.startTime} - ${booking.originalBookingDetails.endTime}`, 20, yPos);
      yPos += 10;
      
      doc.text(`Rescheduled On: ${new Date(booking.originalBookingDetails.rescheduledAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`, 20, yPos);
      yPos += 15;
    }
    
    // Reschedule History
    if (booking.reschedulePayments && booking.reschedulePayments.length > 0) {
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(12);
      doc.setTextColor(255, 165, 0); // Orange color
      doc.text('Reschedule History', 20, yPos);
      yPos += 15;
      
      booking.reschedulePayments.forEach((reschedule, index) => {
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
        doc.text(`Reschedule #${index + 1}`, 20, yPos);
        yPos += 10;
        
        doc.text(`From: ${new Date(reschedule.oldDate).toLocaleDateString()} at ${reschedule.oldStartTime}`, 25, yPos);
        yPos += 8;
        doc.text(`To: ${new Date(reschedule.newDate).toLocaleDateString()} at ${reschedule.newStartTime}`, 25, yPos);
        yPos += 8;
        
        if (reschedule.rescheduleFee > 0 || reschedule.repaymentFee > 0 || reschedule.totalAmount > 0) {
          if (reschedule.rescheduleFee > 0) {
            doc.text(`Reschedule Fee: $${reschedule.rescheduleFee}`, 25, yPos);
            yPos += 8;
          }
          if (reschedule.repaymentFee > 0) {
            doc.text(`Repayment Fee: $${reschedule.repaymentFee}`, 25, yPos);
            yPos += 8;
          }
          doc.text(`Total Reschedule Amount: $${reschedule.totalAmount || reschedule.amount || (reschedule.rescheduleFee + reschedule.repaymentFee)}`, 25, yPos);
          yPos += 8;
          doc.text(`Payment Date: ${new Date(reschedule.paymentDate).toLocaleDateString()}`, 25, yPos);
        } else {
          doc.setTextColor(34, 139, 34); // Green color
          doc.text('Free Reschedule - No additional charges applied', 25, yPos);
          doc.setTextColor(50, 50, 50);
        }
        yPos += 15;
      });
    }
    
    // Cancellation Information
    if (booking.status === 'cancelled') {
      if (yPos > pageHeight - 120) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(12);
      doc.setTextColor(220, 20, 60); // Red color
      doc.text('Cancellation Details', 20, yPos);
      yPos += 15;
      
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      
      if (booking.cancellationDate) {
        doc.text(`Cancellation Date: ${new Date(booking.cancellationDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}`, 20, yPos);
        yPos += 10;
      }
      
      if (booking.cancellationReason) {
        doc.text(`Cancellation Reason: ${booking.cancellationReason}`, 20, yPos);
        yPos += 10;
      }
      
      if (booking.cancellationDetails?.amountDeducted || booking.cancellationFee) {
        doc.setTextColor(220, 20, 60); // Red color
        doc.text(`Amount Deducted: $${(booking.cancellationDetails?.amountDeducted || booking.cancellationFee || 0).toFixed(2)}`, 20, yPos);
        yPos += 10;
        doc.setTextColor(50, 50, 50);
      }
      
      if (booking.cancellationDetails?.amountTransferred || booking.refundAmount) {
        doc.setTextColor(34, 139, 34); // Green color
        doc.text(`Amount Refunded: $${(booking.cancellationDetails?.amountTransferred || booking.refundAmount || 0).toFixed(2)}`, 20, yPos);
        yPos += 10;
        doc.setTextColor(50, 50, 50);
      }
      
      if (booking.cancellationDetails?.transferMethod || booking.refundMethod) {
        const transferMethod = booking.cancellationDetails?.transferMethod === 'original_payment_method' ? 'Original Payment Method' : 
                             booking.cancellationDetails?.transferMethod === 'wallet' ? 'Wallet Credit' : 
                             booking.refundMethod || 'N/A';
        doc.text(`Transfer Method: ${transferMethod}`, 20, yPos);
        yPos += 10;
      }
      
      if (booking.cancellationDetails?.transferDate) {
        doc.text(`Transfer Date: ${new Date(booking.cancellationDetails.transferDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}`, 20, yPos);
        yPos += 10;
      }
      
      if (booking.cancellationDetails?.transferReference) {
        doc.text(`Reference Number: ${booking.cancellationDetails.transferReference}`, 20, yPos);
        yPos += 10;
      }
      
      if (booking.appliedCancelRule) {
        yPos += 5;
        doc.setTextColor(255, 140, 0); // Orange color
        doc.text('Cancellation Policy Applied:', 20, yPos);
        yPos += 8;
        doc.setTextColor(50, 50, 50);
        doc.text(booking.appliedCancelRule.description, 25, yPos);
        yPos += 8;
        doc.text(`Refund: ${booking.appliedCancelRule.creditPercentage}% | Hours before booking: ${booking.appliedCancelRule.hoursBefore}`, 25, yPos);
        yPos += 15;
      }
    }
    
    // Add footer to the last page
    addFooter(doc, doc.getNumberOfPages());
    
    // Save the PDF
    doc.save(`invoice_${booking.bookingNumber || booking.id}.pdf`);
  };

  const BookingDetailsScreen = () => {
    if (!selectedBooking) return null;



    return (
      <Box sx={{ 
        backgroundColor: 'white', 
        minHeight: '100vh',
        overflow: 'auto'
      }}>

        {/* Header */}
        <Box sx={{ 
          backgroundColor: '#1e3a8a', 
          color: 'white', 
          p: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={handleBack}
                  sx={{ color: 'white' }}
                >
                  <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 500, color: 'white' }}>
                  Booking Details
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Receipt />}
                onClick={() => handleDownloadInvoice(selectedBooking)}
                sx={{
                  backgroundColor: 'white',
                  color: '#1e3a8a',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem'
                }}
              >
                Invoice
              </Button>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', ml: 6 }}>
              {selectedBooking.bookingNumber ? `Booking #${selectedBooking.bookingNumber}` : `ID: ${selectedBooking.id}`}
            </Typography>
          </Container>
        </Box>

        <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: 2,
          pb: 8
        }}>
          {/* Status Card */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Booking Status
                </Typography>
                <Chip
                  icon={getStatusIcon(selectedBooking.status)}
                  label={getStatusText(selectedBooking.status)}
                  sx={{
                    backgroundColor: getStatusColor(selectedBooking.status),
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Your booking is {selectedBooking.status}
              </Typography>
            </CardContent>
          </Card>

          {/* Cancellation Details - Show only if booking is cancelled */}
          {selectedBooking.status === 'cancelled' && (
            <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #d32f2f' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#d32f2f' }}>
                  Cancellation Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', minWidth: '120px' }}>
                        Cancellation Date:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedBooking.cancellationDate ? 
                          new Date(selectedBooking.cancellationDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A'
                        }
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', minWidth: '120px' }}>
                        Cancellation Reason:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedBooking.cancellationReason || 'Not specified'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', minWidth: '120px' }}>
                        Amount Deducted:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#d32f2f' }}>
                        ${selectedBooking.cancellationDetails?.amountDeducted || selectedBooking.cancellationFee || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', minWidth: '120px' }}>
                        Amount Refunded:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#2E7D32' }}>
                        ${selectedBooking.cancellationDetails?.amountTransferred || selectedBooking.refundAmount || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', minWidth: '120px' }}>
                        Transfer Method:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedBooking.cancellationDetails?.transferMethod === 'original_payment_method' ? 
                          'Original Payment Method' : 
                          selectedBooking.cancellationDetails?.transferMethod === 'wallet' ? 
                          'Wallet Credit' : 
                          selectedBooking.refundMethod || 'N/A'
                        }
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', minWidth: '120px' }}>
                        Transfer Date:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedBooking.cancellationDetails?.transferDate ? 
                          new Date(selectedBooking.cancellationDetails.transferDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A'
                        }
                      </Typography>
                    </Box>
                  </Grid>
                  
                  {selectedBooking.cancellationDetails?.transferReference && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#666', minWidth: '120px' }}>
                          Reference Number:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500, 
                            fontFamily: 'monospace',
                            backgroundColor: '#f5f5f5',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        >
                          {selectedBooking.cancellationDetails.transferReference}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {selectedBooking.appliedCancelRule && (
                    <Grid item xs={12}>
                      <Box sx={{ 
                        backgroundColor: '#fff3e0', 
                        padding: 2, 
                        borderRadius: 2, 
                        border: '1px solid #ffb74d',
                        mt: 1
                      }}>
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500, mb: 1 }}>
                          Cancellation Policy Applied:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#bf360c' }}>
                          {selectedBooking.appliedCancelRule.description}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#bf360c', fontSize: '12px', mt: 1 }}>
                          Refund: {selectedBooking.appliedCancelRule.creditPercentage}% | 
                          Hours before booking: {selectedBooking.appliedCancelRule.hoursBefore}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}


          {/* Service Details */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Service Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#0A2A66' }}>
                    {(() => {
                      const itemsCount = selectedBooking.items?.length || 0;
                      const servicesCount = selectedBooking.services?.length || 0;
                      const count = itemsCount > 0 ? itemsCount : servicesCount;
                      
                      if (count > 1) {
                        return `${count} Services`;
                      } else if (count === 1) {
                        const item = selectedBooking.items?.[0];
                        const service = selectedBooking.services?.[0];
                        if (item) {
                          return item.serviceId?.serviceName || item.name || 'Service';
                        } else if (service) {
                          return service.name || selectedBooking.serviceName;
                        }
                        return selectedBooking.serviceName;
                      }
                      return selectedBooking.serviceName;
                    })()}
                  </Typography>
                  
                  {/* Use items array first (like ServiceProviderApp), then fallback to services */}
                  {((selectedBooking.items && selectedBooking.items.length > 0) || (selectedBooking.services && selectedBooking.services.length > 0)) && (
                    <Box sx={{ mt: 1 }}>
                      {/* Check if we have items array (like ServiceProviderApp) */}
                      {selectedBooking.items && selectedBooking.items.length > 0 ? (
                        selectedBooking.items.map((item, index) => (
                          <Box key={item.serviceId?._id || item._id || index} sx={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: 2, 
                            borderRadius: 2, 
                            mb: 1,
                            border: '1px solid #e9ecef',
                            borderLeft: `4px solid ${item.serviceId?.colorHex || '#0A2A66'}`
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: '#0A2A66' }}>
                                {item.serviceId?.serviceName || item.name || 'Service'}
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                                ${item.price || 0}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                Duration: {item.serviceId?.duration || item.duration || 60} min
                              </Typography>
                              {item.quantity && item.quantity > 1 && (
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                  Qty: {item.quantity}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))
                      ) : (
                        /* Fallback to services array */
                        selectedBooking.services?.map((service, index) => (
                          <Box key={service.id || index} sx={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: 2, 
                            borderRadius: 2, 
                            mb: 1,
                            border: '1px solid #e9ecef'
                          }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#0A2A66', mb: 0.5 }}>
                              {service.name}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                Duration: {service.duration || 60} min
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                                ${service.price}
                              </Typography>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarToday sx={{ color: '#006AF9', fontSize: 20 }} />
                    <Typography variant="body1">
                      {new Date(selectedBooking.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessTime sx={{ color: '#006AF9', fontSize: 20 }} />
                    <Typography variant="body1">
                      {selectedBooking.time} ({selectedBooking.duration} min)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationOn sx={{ color: '#006AF9', fontSize: 20 }} />
                    <Typography variant="body1">
                      {selectedBooking.providerType === 'salon' ? 'Salon Address' : 'Service Address'}: {selectedBooking.providerAddress}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {selectedBooking.serviceType === 'in_salon' ? (
                      <Store sx={{ color: '#006AF9', fontSize: 20 }} />
                    ) : (
                      <Home sx={{ color: '#006AF9', fontSize: 20 }} />
                    )}
                    <Typography variant="body1">
                      Service Type: {selectedBooking.serviceType === 'in_salon' ? 'In Salon' : 'At Home'}
                    </Typography>
                  </Box>
                </Grid>
                {selectedBooking.serviceAddress && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <LocationOn sx={{ color: '#006AF9', fontSize: 20 }} />
                      <Typography variant="body1">
                        Service Address: {selectedBooking.serviceAddress}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Provider Details */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Service Provider
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Person sx={{ color: '#006AF9', fontSize: 20 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedBooking.providerName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Phone sx={{ color: '#006AF9', fontSize: 20 }} />
                    <Typography variant="body1">
                      {selectedBooking.providerPhone}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Email sx={{ color: '#006AF9', fontSize: 20 }} />
                    <Typography variant="body1">
                      {selectedBooking.providerAddress}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Payment Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    Amount
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0A2A66' }}>
                    ${selectedBooking.total.toFixed(2)}
                  </Typography>
                </Grid>
                
                {/* Coupon Discount Line Item */}
                {(selectedBooking?.couponDiscount > 0) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: '#28a745' }}>
                      🎉 Coupon Discount ({selectedBooking?.appliedCoupon}):
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                      -${(selectedBooking?.couponDiscount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                )}
                
                {/* Wallet Applied Line Item */}
                {(selectedBooking?.walletBalanceUsed > 0 || selectedBooking?.appliedWalletBalance > 0) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: '#28a745' }}>
                      💳 Wallet Applied:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                      -${(selectedBooking?.walletBalanceUsed || selectedBooking?.appliedWalletBalance || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                )}

                {/* Gems Applied Line Item */}
                {(selectedBooking?.gemsUsed > 0 || selectedBooking?.appliedGems > 0) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: '#28a745' }}>
                      💎 {selectedBooking?.gemsUsed || selectedBooking?.appliedGems || 0} gems used -${(((selectedBooking?.gemsUsed || selectedBooking?.appliedGems || 0) / 1000) * 10).toFixed(2)}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    Payment Method
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedBooking.paymentMethod === 'card' ? 'Credit Card' : 
                     selectedBooking.paymentMethod === 'wallet' ? 'Wallet' :
                     selectedBooking.paymentMethod === 'cash' ? 'Cash' :
                     selectedBooking.paymentMethod === 'bank_transfer' ? 'Bank Transfer' :
                     'Credit Card'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    Payment Confirmation
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                    {selectedBooking.stripePaymentIntentId || selectedBooking.paymentConfirmationNumber || selectedBooking.paymentConfirmationId || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Payment sx={{ color: '#006AF9', fontSize: 20 }} />
                    <Typography variant="body1">
                      Status: <Chip 
                        label={selectedBooking.paymentStatus?.toUpperCase() || 'PAID'} 
                        size="small"
                        color={selectedBooking.paymentStatus === 'completed' ? 'success' : 'default'}
                      />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Original Booking Details - Show if booking has been rescheduled */}
          {selectedBooking.originalBookingDetails && (
            <Card sx={{ mb: 3, borderRadius: 3, border: '2px solid #6b7280' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: '#6b7280' }}>
                  📋 Original Appointment Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CalendarToday sx={{ color: '#6b7280', fontSize: 20 }} />
                      <Typography variant="body1">
                        <strong>Date:</strong> {new Date(selectedBooking.originalBookingDetails.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <AccessTime sx={{ color: '#6b7280', fontSize: 20 }} />
                      <Typography variant="body1">
                        <strong>Time:</strong> {selectedBooking.originalBookingDetails.startTime} - {selectedBooking.originalBookingDetails.endTime}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule sx={{ color: '#6b7280', fontSize: 20 }} />
                      <Typography variant="body1">
                        <strong>Rescheduled on:</strong> {new Date(selectedBooking.originalBookingDetails.rescheduledAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Reschedule History - Only show if this booking has reschedule payments */}
          {selectedBooking.reschedulePayments && selectedBooking.reschedulePayments.length > 0 && (
            <Card sx={{ mb: 3, borderRadius: 3, border: '2px solid #f59e0b' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: '#f59e0b' }}>
                  🔄 Reschedule History
                </Typography>
                
                {selectedBooking.reschedulePayments.map((reschedule, index) => (
                  <Box key={index} sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: '#fef3c7', 
                    borderRadius: 2, 
                    border: '1px solid #f59e0b' 
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#92400e' }}>
                      Reschedule #{index + 1}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {/* Old Appointment */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, backgroundColor: '#fef3c7', borderRadius: 1, border: '1px solid #f59e0b' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: '#92400e', fontWeight: 'bold' }}>
                            📅 Original Appointment
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Date:</strong> {new Date(reschedule.oldDate).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Time:</strong> {reschedule.oldStartTime}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Duration:</strong> {selectedBooking.duration || 'N/A'} minutes
                          </Typography>
                        </Box>
                      </Grid>

                      {/* New Appointment */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, backgroundColor: '#d1fae5', borderRadius: 1, border: '1px solid #10b981' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: '#065f46', fontWeight: 'bold' }}>
                            ✅ New Appointment
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Date:</strong> {new Date(reschedule.newDate).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Time:</strong> {reschedule.newStartTime}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Duration:</strong> {selectedBooking.duration || 'N/A'} minutes
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Reschedule Payment Details */}
                    {(reschedule.rescheduleFee > 0 || reschedule.repaymentFee > 0 || reschedule.totalAmount > 0 || reschedule.amount > 0) && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f3f4f6', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          💳 Reschedule Payment Details
                        </Typography>
                        <Grid container spacing={2}>
                          {/* Reschedule Fee */}
                          {reschedule.rescheduleFee > 0 && (
                            <>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Reschedule Fee:</strong> ${reschedule.rescheduleFee}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Reschedule Payment ID:</strong> {reschedule.paymentConfirmationId || 'N/A'}
                                </Typography>
                              </Grid>
                            </>
                          )}
                          
                          {/* Repayment Fee */}
                          {reschedule.repaymentFee > 0 && (
                            <>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Repayment Fee:</strong> ${reschedule.repaymentFee}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Repayment Payment ID:</strong> {reschedule.repaymentPaymentConfirmationId || 'N/A'}
                                </Typography>
                              </Grid>
                            </>
                          )}
                          
                          {/* Total Amount */}
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              <strong>Total Amount:</strong> ${reschedule.totalAmount || reschedule.amount || (reschedule.rescheduleFee + reschedule.repaymentFee)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Payment Date:</strong> {new Date(reschedule.paymentDate).toLocaleDateString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Payment Method:</strong> {reschedule.paymentMethod || 'Card'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {/* Free Reschedule Notice */}
                    {(reschedule.amount === 0 && reschedule.totalAmount === 0 && reschedule.rescheduleFee === 0 && reschedule.repaymentFee === 0) && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#d1fae5', borderRadius: 1, border: '1px solid #10b981' }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#065f46' }}>
                          🎉 Free Reschedule - No additional charges applied
                        </Typography>
                      </Box>
                    )}

                    {/* Reschedule Reason */}
                    {reschedule.reason && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Reason:</strong> {reschedule.reason}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {selectedBooking.additionalNotes && (
            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Additional Notes
                </Typography>
                <Typography variant="body1" sx={{ color: '#666666' }}>
                  {selectedBooking.additionalNotes}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons - Only show for confirmed bookings that are today or in the future and not completed */}
          {shouldShowActionButtons(selectedBooking) && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              {/* Reschedule Button - Only show if not already rescheduled */}
              {!selectedBooking.reschedulePayments?.length && (
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleReschedule(selectedBooking)}
                sx={{ 
                  borderRadius: 2,
                  backgroundColor: '#006AF9',
                  '&:hover': {
                    backgroundColor: '#0056b3'
                  }
                }}
              >
                Reschedule
              </Button>
              )}
              
              {/* Cancel Button - Show for confirmed bookings that are today or in the future */}
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleCancel(selectedBooking)}
                sx={{ 
                  borderRadius: 2,
                  backgroundColor: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#b71c1c'
                  }
                }}
              >
                Cancel Booking
              </Button>
            </Box>
          )}

          {/* Message for past bookings */}
          {selectedBooking.status === 'confirmed' && !isBookingTodayOrFuture(selectedBooking.date) && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #dee2e6' }}>
              <Typography variant="body2" sx={{ color: '#6c757d', textAlign: 'center' }}>
                📅 This is a past booking. Reschedule and cancel options are not available.
              </Typography>
            </Box>
          )}

          {/* Already Rescheduled Message */}
          {selectedBooking.status === 'confirmed' && selectedBooking.reschedulePayments?.length > 0 && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #dee2e6' }}>
              <Typography variant="body2" sx={{ color: '#6c757d', textAlign: 'center' }}>
                ⚠️ This booking has already been rescheduled and cannot be rescheduled again.
              </Typography>
            </Box>
          )}

          {/* Review Buttons - Show for completed bookings */}
          {selectedBooking.status === 'completed' && (
            <Box sx={{ mb: 3 }}>
              {(() => {
                const bookingKey = selectedBooking.id || selectedBooking._id;
                const hasReview = bookingReviews[bookingKey];
                return !hasReview;
              })() ? (
                // No review exists - show "Review" button
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleLeaveReview(selectedBooking)}
                  disabled={reviewLoading}
                  startIcon={<RateReview sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                  sx={{
                    backgroundColor: '#FFB400', 
                    color: '#000',
                    fontSize: { xs: '13px', sm: '15px' },
                    py: { xs: 1.5, sm: 2 }, 
                    px: { xs: 3, sm: 4 },
                    borderRadius: 2, 
                    textTransform: 'none', 
                    fontWeight: 700,
                    height: { xs: '44px', sm: '48px' },
                    boxShadow: '0 2px 4px rgba(255, 180, 0, 0.3)',
                    '&:hover': { 
                      backgroundColor: '#FFA000', 
                      boxShadow: '0 4px 8px rgba(255, 180, 0, 0.4)', 
                      transform: 'translateY(-1px)' 
                    },
                    '&:active': { transform: 'translateY(0)' },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Review
                </Button>
              ) : (
                // Show "Edit" and "Delete" buttons if review exists
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleEditReview(selectedBooking)}
                    disabled={reviewLoading}
                    startIcon={<Edit sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                    sx={{
                      borderColor: '#006AF9', 
                      color: '#006AF9',
                      fontSize: { xs: '12px', sm: '14px' },
                      py: { xs: 1.2, sm: 1.5 }, 
                      px: { xs: 2.5, sm: 3 },
                      borderRadius: 2, 
                      textTransform: 'none', 
                      fontWeight: 600,
                      height: { xs: '40px', sm: '44px' },
                      borderWidth: 2,
                      '&:hover': { 
                        borderColor: '#0056b3', 
                        backgroundColor: 'rgba(0, 106, 249, 0.08)', 
                        borderWidth: 2, 
                        transform: 'translateY(-1px)', 
                        boxShadow: '0 2px 8px rgba(0, 106, 249, 0.2)' 
                      },
                      '&:active': { transform: 'translateY(0)' },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    Edit Review
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const bookingKey = selectedBooking.id || selectedBooking._id;
                      const existingReview = bookingReviews[bookingKey];
                      
                      if (!existingReview) return;
                      
                      setReviewToDelete({ booking: selectedBooking, review: existingReview });
                      setShowDeleteDialog(true);
                    }}
                    disabled={reviewLoading}
                    startIcon={<Delete sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                    sx={{
                      borderColor: '#d32f2f', 
                      color: '#d32f2f',
                      fontSize: { xs: '12px', sm: '14px' },
                      py: { xs: 1.2, sm: 1.5 }, 
                      px: { xs: 2.5, sm: 3 },
                      borderRadius: 2, 
                      textTransform: 'none', 
                      fontWeight: 600,
                      height: { xs: '40px', sm: '44px' },
                      borderWidth: 2,
                      zIndex: 9999,
                      position: 'relative',
                      '&:hover': { 
                        borderColor: '#b71c1c', 
                        backgroundColor: 'rgba(211, 47, 47, 0.08)', 
                        borderWidth: 2, 
                        transform: 'translateY(-1px)', 
                        boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)' 
                      },
                      '&:active': { transform: 'translateY(0)' },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    Delete Review
                  </Button>
                </Box>
              )}
              
              {/* Review Status Message */}
              {(() => {
                const bookingKey = selectedBooking.id || selectedBooking._id;
                const review = bookingReviews[bookingKey];
                return review && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 2, border: '1px solid #4caf50' }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CheckCircle fontSize="small" />
                      You've reviewed this booking
                      {review.rating && (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                          ({review.rating} <Star fontSize="small" sx={{ color: '#FFB400', ml: 0.5 }} />)
                        </Box>
                      )}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Separate bookings into past and upcoming
  const upcomingBookings = bookings.filter(booking => isBookingTodayOrFuture(booking.date));
  const pastBookings = bookings.filter(booking => !isBookingTodayOrFuture(booking.date));

  // Filter and sort bookings based on active tab
  const filteredAndSortedBookings = (activeTab === 'upcoming' ? upcomingBookings : pastBookings)
    .filter(booking => {
      const matchesStatus = statusFilter === 'All' || getStatusText(booking.status) === statusFilter;
      const matchesSearch = booking.serviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           booking.providerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           booking.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (booking.services && booking.services.some(service => 
                             service.name.toLowerCase().includes(searchTerm.toLowerCase())
                           ));
      const matchesDate = !dateFilter || new Date(booking.date).toDateString() === dateFilter.toDateString();
      return matchesStatus && matchesSearch && matchesDate;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.date) - new Date(b.date); // Ascending: nearest date first
        case 'price':
          return b.price - a.price;
        case 'name':
          const aName = a.services?.length > 1 ? a.services[0].name : a.serviceName;
          const bName = b.services?.length > 1 ? b.services[0].name : b.serviceName;
          return aName.localeCompare(bName);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

  // Remove the conflicting first return statement - this was causing the issue



  const RescheduleDialog = () => {
    const rescheduleRule = selectedBooking ? getApplicableRescheduleRule(selectedBooking) : null;
    const rescheduleFee = selectedBooking ? calculateRescheduleFee(selectedBooking) : 0;



    const handleRescheduleAction = () => {
      setShowRescheduleDialog(false);
      
      
      // Create a properly formatted originalBooking object with all necessary fields
      const originalBooking = {
        id: selectedBooking._id || selectedBooking.id, // Use _id if id is not available
        bookingNumber: selectedBooking.bookingNumber,
        date: selectedBooking.date,
        startTime: selectedBooking.time, // Use the time field as startTime
        endTime: selectedBooking.endTime || calculateEndTime(selectedBooking.time, selectedBooking.duration),
        duration: selectedBooking.duration,
        providerId: selectedBooking.providerId,
        totalAmount: selectedBooking.totalAmount || selectedBooking.total || selectedBooking.price,
        status: selectedBooking.status,
        services: selectedBooking.services?.length > 0 ? selectedBooking.services : (selectedBooking.items || []).map(item => ({
          id: item.serviceId?._id,
          name: item.serviceId?.serviceName,
          price: item.price,
          duration: parseInt(item.serviceId?.duration || item.duration || 30, 10) || 30,
          image: item.serviceId?.imagePaths?.[0] || 'https://via.placeholder.com/80x80'
        })).filter(service => service.id && service.name)
      };
      
      
      
      // Navigate to availability screen with reschedule parameters
      const providerId = selectedBooking.providerId?._id ? String(selectedBooking.providerId._id) : String(selectedBooking.providerId);
      
      navigate('/availability/' + providerId, {
        state: {
          isReschedule: true,
          originalBooking: originalBooking,
          rescheduleFee: rescheduleFee,
          rescheduleRule: rescheduleRule,
          cartItems: originalBooking.services
        }
      });
    };

    return (
      <Dialog open={showRescheduleDialog} onClose={() => setShowRescheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
            Reschedule Booking
          </Typography>
          <IconButton 
            onClick={() => setShowRescheduleDialog(false)}
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {selectedBooking && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1.5, color: '#1e3a8a', fontWeight: 600, fontSize: '16px' }}>
                Reschedule Rules
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '12px', color: '#666' }}>
                  Hours until booking: {calculateHoursUntilBooking(selectedBooking.date, selectedBooking.time)}
                </Typography>
              </Box>

              {/* All Rules - Compact */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reschedulingRules.map((rule, index) => {
                  const isApplicable = rule === rescheduleRule;
                  return (
                    <Paper
                      key={index}
                      sx={{
                        p: 1.5,
                        border: `1px solid ${isApplicable ? '#2E7D32' : '#E5E7EB'}`,
                        backgroundColor: isApplicable ? '#F1F8E9' : '#FAFAFA',
                        borderRadius: 1.5,
                        position: 'relative'
                      }}
                    >
                      {isApplicable && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -6,
                            left: 12,
                            backgroundColor: '#2E7D32',
                            color: 'white',
                            px: 1.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            fontSize: '10px',
                            fontWeight: 600
                          }}
                        >
                          APPLICABLE
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: isApplicable ? '#2E7D32' : '#666'
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isApplicable ? '#2E7D32' : '#333',
                            fontSize: '13px'
                          }}
                        >
                          {rule.hoursBefore === 0 ? 'Within 6 hours' : `${rule.hoursBefore}+ hours before`}
                        </Typography>
                      </Box>
                      
                      <Typography variant="caption" sx={{ color: '#666', fontSize: '11px', lineHeight: 1.3 }}>
                        {rule.description}
                      </Typography>
                      
                      {rule.allowReschedule && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '11px' }}>
                            Fee:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: rule.feePercentage === 0 ? '#2E7D32' : '#FF9800',
                              fontSize: '11px'
                            }}
                          >
                            {rule.feePercentage === 0 ? 'Free' : `${rule.feePercentage}%`}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>

              {/* Current Booking Info - Compact */}
              {rescheduleRule && (
                <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#F8F9FA', borderRadius: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, fontSize: '12px' }}>
                    Current Booking Summary
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 0.5, fontSize: '11px', display: 'block' }}>
                    Service: {selectedBooking.services?.length > 1 
                      ? `${selectedBooking.services.length} Services` 
                      : selectedBooking.serviceName}
                  </Typography>
                  {selectedBooking.services?.length > 1 && (
                    <Box sx={{ ml: 1, mb: 0.5 }}>
                      {selectedBooking.services.map((service, index) => (
                        <Typography key={index} variant="caption" sx={{ display: 'block', fontSize: '10px', color: '#666' }}>
                          • {service.name}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ mb: 0.5, fontSize: '11px', display: 'block' }}>
                    Date: {new Date(selectedBooking.date).toLocaleDateString()} at {selectedBooking.time}
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 0.5, fontSize: '11px', display: 'block' }}>
                    Amount Paid: ${(selectedBooking.total || selectedBooking.totalAmount || selectedBooking.price || selectedBooking.subTotal).toFixed(2)}
                  </Typography>
                  {rescheduleFee > 0 && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.5, fontSize: '11px' }}>
                      <Typography variant="caption" sx={{ fontSize: '11px' }}>
                        Reschedule fee: ${rescheduleFee.toFixed(2)}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setShowRescheduleDialog(false)}
            size="small"
            sx={{ fontSize: '13px' }}
          >
            Cancel
          </Button>
          {selectedBooking && getApplicableRescheduleRule(selectedBooking)?.allowReschedule && (
            <Button 
              variant="contained" 
              onClick={handleRescheduleAction}
              size="small"
              sx={{ 
                backgroundColor: '#006AF9',
                fontSize: '13px',
                px: 2
              }}
            >
              {rescheduleFee > 0 ? `Pay $${rescheduleFee.toFixed(2)} & Continue` : 'Continue to Reschedule'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const CancelDialog = () => {
    const cancelRule = selectedBooking ? getApplicableCancelRule(selectedBooking) : null;
    const refundAmount = selectedBooking ? calculateCancelRefund(selectedBooking) : 0;
    

    return (
      <Dialog open={showCancelDialog} onClose={() => !cancelLoading && setShowCancelDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#EF4444', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
          Cancel Booking
          </Typography>
          <IconButton 
            onClick={() => setShowCancelDialog(false)}
            disabled={cancelLoading}
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {selectedBooking && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1.5, color: '#1e3a8a', fontWeight: 600 }}>
                Cancellation Rules
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: '#666' }}>
                  Hours until booking: {calculateHoursUntilBooking(selectedBooking.date, selectedBooking.time)}
                </Typography>
              </Box>

              {/* All Rules */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {cancellationRules.map((rule, index) => {
                  const isApplicable = rule === cancelRule;
                  return (
                    <Paper
                      key={index}
                      sx={{
                        p: 1.5,
                        border: `2px solid ${isApplicable ? '#2E7D32' : '#E5E7EB'}`,
                        backgroundColor: isApplicable ? '#F1F8E9' : '#FAFAFA',
                        borderRadius: 2,
                        position: 'relative'
                      }}
                    >
                      {isApplicable && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -6,
                            left: 12,
                            backgroundColor: '#2E7D32',
                            color: 'white',
                            px: 1.5,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '10px',
                            fontWeight: 600
                          }}
                        >
                          APPLICABLE
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: isApplicable ? '#2E7D32' : '#666'
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isApplicable ? '#2E7D32' : '#333'
                          }}
                        >
                          {rule.hoursBefore === 0 ? 'Within 6 hours' : `${rule.hoursBefore}+ hours before`}
                        </Typography>
                      </Box>
                      
                      <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>
                        {rule.description}
                      </Typography>
                      
                      {rule.refundAllowed && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            Refund:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: rule.creditPercentage === 100 ? '#2E7D32' : '#FF9800'
                            }}
                          >
                            {rule.creditPercentage}%
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>

              {/* Current Booking Info */}
              {cancelRule && (
                <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#F8F9FA', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Current Booking Summary
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                    Service: {selectedBooking.services?.length > 1 
                      ? `${selectedBooking.services.length} Services` 
                      : selectedBooking.serviceName}
                  </Typography>
                  {selectedBooking.services?.length > 1 && (
                    <Box sx={{ ml: 1, mb: 0.5 }}>
                      {selectedBooking.services.map((service, index) => (
                        <Typography key={index} variant="caption" sx={{ display: 'block', fontSize: '10px', color: '#666' }}>
                          • {service.name}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                    Date: {new Date(selectedBooking.date).toLocaleDateString()} at {selectedBooking.time}
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                    Amount Paid: ${(selectedBooking.total || selectedBooking.totalAmount || selectedBooking.price || selectedBooking.subTotal).toFixed(2)}
                  </Typography>
                  {cancelRule.refundAllowed && (
                    <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                      <Typography variant="caption">
                        Refund amount: ${refundAmount.toFixed(2)} (${cancelRule.creditPercentage}% of ${(selectedBooking.total || selectedBooking.totalAmount || selectedBooking.price || selectedBooking.subTotal).toFixed(2)})
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {/* Refund Method Selection */}
              {cancelRule && cancelRule.refundAllowed && refundAmount > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Select Refund Method
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {/* Wallet Option */}
                    <Box
                      sx={{
                        flex: 1,
                        p: 1.5,
                        border: `2px solid ${refundMethod === 'wallet' ? '#1976D2' : '#E0E0E0'}`,
                        borderRadius: 2,
                        backgroundColor: refundMethod === 'wallet' ? '#E3F2FD' : '#FAFAFA',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#1976D2',
                          backgroundColor: '#E3F2FD'
                        }
                      }}
                      onClick={() => setRefundMethod('wallet')}
                    >
                      <Box sx={{ mb: 0.5 }}>
                        <AccountCircle sx={{ fontSize: 24, color: '#1976D2' }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976D2' }}>
                        Wallet
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Get refund in your wallet
                      </Typography>
                    </Box>

                    {/* Original Payment Method Option */}
                    <Box
                      sx={{
                        flex: 1,
                        p: 1.5,
                        border: `2px solid ${refundMethod === 'original' ? '#1976D2' : '#E0E0E0'}`,
                        borderRadius: 2,
                        backgroundColor: refundMethod === 'original' ? '#E3F2FD' : '#FAFAFA',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#1976D2',
                          backgroundColor: '#E3F2FD'
                        }
                      }}
                      onClick={() => setRefundMethod('original')}
                    >
                      <Box sx={{ mb: 0.5 }}>
                        <Receipt sx={{ fontSize: 24, color: '#1976D2' }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976D2' }}>
                        Original Payment Method
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Refund to original card/payment
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowCancelDialog(false)}
            disabled={cancelLoading}
          >
            Keep Booking
          </Button>
          {selectedBooking && getApplicableCancelRule(selectedBooking)?.refundAllowed && (
            <Button 
              variant="contained" 
              onClick={handleCancelBooking}
              disabled={cancelLoading}
              startIcon={cancelLoading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ backgroundColor: '#EF4444' }}
            >
              {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };



  // Success Dialog Component
  const SuccessDialog = () => {
    const isSuccess = !successMessage.toLowerCase().includes('failed');
    
    return (
      <Dialog 
        open={showSuccessDialog} 
        onClose={() => {
          setShowSuccessDialog(false);
          setSuccessMessage('');
          // Redirect back to bookings listing
          setShowBookingDetails(false);
          setSelectedBooking(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            {isSuccess ? (
              <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
            ) : (
              <Error sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
            )}
          </Box>
          
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: isSuccess ? '#4caf50' : '#f44336' }}>
            {isSuccess ? 'Success!' : 'Error'}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
            {successMessage}
          </Typography>
          
          <Button
            variant="contained"
            onClick={() => {
              setShowSuccessDialog(false);
              setSuccessMessage('');
              // Redirect back to bookings listing
              setShowBookingDetails(false);
              setSelectedBooking(null);
            }}
            sx={{
              backgroundColor: isSuccess ? '#4caf50' : '#f44336',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: isSuccess ? '#45a049' : '#d32f2f'
              }
            }}
          >
            Back to Bookings
          </Button>
        </DialogContent>
      </Dialog>
    );
  };

  // Render booking details screen if active
  if (showBookingDetails && selectedBooking) {
  return (
    <Box sx={{ pb: 8, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>

        
        <BookingDetailsScreen />
        <RescheduleDialog />
        <CancelDialog />
        <SuccessDialog />
        
        {/* Delete Review Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Delete sx={{ color: '#d32f2f', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Delete Review
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this review?
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              This action cannot be undone. Your review will be permanently removed.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
            <Button
              onClick={() => setShowDeleteDialog(false)}
              variant="outlined"
              disabled={reviewLoading}
              sx={{ 
                borderColor: '#666', 
                color: '#666',
                '&:hover': {
                  borderColor: '#333',
                  backgroundColor: 'rgba(102, 102, 102, 0.04)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteReview}
              variant="contained"
              disabled={reviewLoading}
              startIcon={reviewLoading ? <CircularProgress size={16} color="inherit" /> : <Delete />}
              sx={{ 
                backgroundColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#b71c1c'
                }
              }}
            >
              {reviewLoading ? 'Deleting...' : 'Delete Review'}
            </Button>
          </DialogActions>
        </Dialog>
        
        <BottomNav />
      </Box>
    );
  }

  // Show loading state when URL booking is being loaded OR when loading initial data with URL param
  if ((urlLoading && urlBookingId) || (urlBookingId && loading)) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <BrandLoader />
        <Typography variant="body2" sx={{ color: '#666', mt: 2 }}>
          Loading booking details...
        </Typography>
      </Box>
    );
  }

  // Show booking details if requested
  if (showBookingDetails && selectedBooking) {
    return <BookingDetailsScreen />;
  }

  // Render main booking listing
  return (
    <Box sx={{ pb: 8, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>

      {/* Loading State */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '200px',
          flexDirection: 'column',
          gap: 2
        }}>
          <BrandLoader />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '200px',
          flexDirection: 'column',
          gap: 2
        }}>
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
            <Button 
              onClick={fetchBookings} 
              sx={{ mt: 1 }}
              variant="outlined"
            >
              Try Again
            </Button>
          </Alert>
        </Box>
      )}
      
      {/* Content - Only show when not loading */}
      {!loading && !error && (
        <>
          {/* Header - Dark Blue */}
          <Box sx={{ 
            backgroundColor: '#1e3a8a', 
            color: 'white', 
            p: 1.5,
            position: 'sticky',
            top: 0,
            zIndex: 100,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <IconButton 
                  onClick={handleBack}
                  sx={{ color: 'white' }}
                >
                  <ArrowBack />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'white' }}>
                  My Bookings
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', ml: 6, fontSize: '12px' }}>
                Manage your appointments and services
              </Typography>

            </Container>
          </Box>

          <Box sx={{ 
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            width: '100%',
            backgroundColor: '#f5f5f5',
            mt: 2
          }}>
        {/* Horizontal Filter Chips - Blue */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none'
          }}>
            {statusOptions.map((status) => (
              <Chip
                key={status.name}
                label={status.name}
                onClick={() => setStatusFilter(status.name)}
                sx={{
                  backgroundColor: statusFilter === status.name ? status.color : '#f0f0f0',
                  color: statusFilter === status.name ? 'white' : '#666',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  border: statusFilter === status.name ? `1px solid ${status.color}` : '1px solid #ddd',
                  '&:hover': {
                    backgroundColor: statusFilter === status.name ? status.color : '#e0e0e0'
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Search and Sort Bar */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#006AF9' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Filter by date"
                value={dateFilter ? dateFilter.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateFilter(e.target.value ? new Date(e.target.value) : null)}
                onClick={(e) => {
                  // Open date picker when clicking anywhere in the field
                  e.target.showPicker && e.target.showPicker();
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ 
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    cursor: 'pointer'
                  },
                  '& .MuiInputBase-input': {
                    cursor: 'pointer'
                  }
                }}
              />
              {dateFilter && (
                <Button
                  size="small"
                  onClick={() => setDateFilter(null)}
                  sx={{ mt: 1, fontSize: '12px' }}
                >
                  Clear Date Filter
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small" sx={{ backgroundColor: 'white' }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="price">Price</MenuItem>
                  <MenuItem value="name">Service Name</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Booking Tabs */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant={activeTab === 'upcoming' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('upcoming')}
              sx={{
                flex: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: activeTab === 'upcoming' ? '#006AF9' : 'transparent',
                borderColor: '#006AF9',
                color: activeTab === 'upcoming' ? 'white' : '#006AF9',
                '&:hover': {
                  backgroundColor: activeTab === 'upcoming' ? '#0056b3' : 'rgba(0, 106, 249, 0.04)'
                }
              }}
            >
              📅 Upcoming ({upcomingBookings.length})
            </Button>
            <Button
              variant={activeTab === 'past' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('past')}
              sx={{
                flex: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: activeTab === 'past' ? '#006AF9' : 'transparent',
                borderColor: '#006AF9',
                color: activeTab === 'past' ? 'white' : '#006AF9',
                '&:hover': {
                  backgroundColor: activeTab === 'past' ? '#0056b3' : 'rgba(0, 106, 249, 0.04)'
                }
              }}
            >
              📋 Past ({pastBookings.length})
            </Button>
          </Box>
        </Box>

        {/* Bookings List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredAndSortedBookings.map((booking) => (
            <Paper
              key={booking.id}
              elevation={2}
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                },
                borderRadius: 3,
                overflow: 'hidden',
                background: booking.status === 'cancelled' 
                  ? 'linear-gradient(135deg, #ffebee 0%, #fce4ec 100%)'
                  : !isBookingTodayOrFuture(booking.date)
                    ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' // Past booking - muted
                    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', // Future booking - bright
                border: booking.status === 'cancelled' 
                  ? '2px solid #d32f2f'
                  : !isBookingTodayOrFuture(booking.date)
                    ? '1px solid #adb5bd' // Past booking - muted border
                    : '1px solid #e3e6ea', // Future booking - normal border
                opacity: !isBookingTodayOrFuture(booking.date) ? 0.8 : 1 // Slightly fade past bookings
              }}
              onClick={() => handleBookingClick(booking)}
            >
              <Box sx={{ p: 3 }}>
                {/* Header with status */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ fontSize: 18, color: '#006AF9' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 500 }}>
                      {booking.bookingNumber ? `Booking #${booking.bookingNumber}` : `ID: ${booking.id}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={getStatusText(booking.status)}
                    size="small"
                    sx={{ 
                      backgroundColor: getStatusColor(booking.status),
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 600,
                      height: 26,
                      borderRadius: 2
                    }}
                  />
                    {booking.status === 'cancelled' && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#d32f2f', 
                          fontWeight: 600,
                          fontSize: '10px',
                          backgroundColor: '#ffebee',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          border: '1px solid #ffcdd2'
                        }}
                      >
                        CANCELLED
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Main content */}
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {/* Provider Profile Image */}
                  <Box sx={{ 
                    width: 60, 
                    height: 60, 
                    borderRadius: 3,
                    border: '2px solid #e3e6ea',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProviderProfile(booking);
                  }}
                  >
                    <Avatar 
                      src={booking.providerProfileImage} 
                      sx={{ width: 60, height: 60 }}
                      onError={(e) => {
                        // Profile image failed to load
                      }}
                      onLoad={() => {
                        // Profile image loaded successfully
                      }}
                    >
                      <AccountCircle sx={{ fontSize: 60, color: '#006AF9' }} />
                    </Avatar>
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Provider name */}
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '18px', color: '#1a1a1a', mb: 0.5 }}>
                      {booking.providerName}
                    </Typography>
                    
                    {/* Service name */}
                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 600, color: '#333', mb: 0.5 }}>
                      {booking.serviceName}
                    </Typography>
                    {booking.services?.length > 1 && (
                      <Box sx={{ mb: 0.5 }}>
                        {booking.services.slice(0, 2).map((service, index) => (
                          <Typography key={index} variant="caption" sx={{ display: 'block', color: '#666', fontSize: '11px', lineHeight: 1.2 }}>
                            • {service.name}
                          </Typography>
                        ))}
                        {booking.services.length > 2 && (
                          <Typography variant="caption" sx={{ color: '#999', fontSize: '10px' }}>
                            +{booking.services.length - 2} more services
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Provider type icon and service type */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      {booking.providerType === 'salon' ? (
                        <Store sx={{ fontSize: 14, color: '#006AF9' }} />
                      ) : (
                        <Person sx={{ fontSize: 14, color: '#006AF9' }} />
                      )}
                      <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, color: '#006AF9' }}>
                        {booking.serviceType === 'in_salon' ? 'In Salon' : 'At Home'}
                      </Typography>
                    </Box>
                    
                    {/* Duration */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <AccessTime sx={{ fontSize: 14, color: '#006AF9' }} />
                      <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, color: '#666' }}>
                        Duration: {booking.duration} mins
                      </Typography>
                    </Box>
                    
                    {/* Address - Show appropriate address label based on provider type */}
                    {booking.providerAddress ? (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                        <LocationOn sx={{ fontSize: 14, color: '#006AF9', mt: 0.1 }} />
                        <Typography variant="body2" sx={{ fontSize: '11px', color: '#666', lineHeight: 1.2 }}>
                          {booking.providerType === 'salon' ? 'Salon: ' : 'Service: '}{booking.providerAddress}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <LocationOn sx={{ fontSize: 14, color: '#FF0000' }} />
                        <Typography variant="body2" sx={{ fontSize: '11px', color: '#FF0000', lineHeight: 1.2 }}>
                          No {booking.providerType === 'salon' ? 'salon' : 'service'} address
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Booking date & time */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Event sx={{ fontSize: 14, color: '#006AF9' }} />
                      <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, color: '#666' }}>
                        {new Date(booking.date).toLocaleDateString()} at {booking.time}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Price and actions */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '20px', color: '#1a1a1a' }}>
                      ${parseFloat(booking.price).toFixed(2)}
                    </Typography>
                    
                    {/* Cancellation summary for cancelled bookings */}
                    {booking.status === 'cancelled' && (
                      <Box sx={{ 
                        backgroundColor: '#ffebee', 
                        padding: '8px 12px', 
                        borderRadius: '8px',
                        border: '1px solid #ffcdd2',
                        mt: 1,
                        minWidth: '120px'
                      }}>
                        <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600, display: 'block', mb: 0.5 }}>
                          Cancelled
                        </Typography>
                        {booking.cancellationDetails && (
                          <>
                            <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '10px' }}>
                              Refund: ${booking.cancellationDetails.amountTransferred || booking.refundAmount || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '10px' }}>
                              Fee: ${booking.cancellationDetails.amountDeducted || booking.cancellationFee || 0}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                    
                    {/* Chat or location icon below price - for ongoing bookings */}
                    <Box sx={{ mt: 1 }}>
                      {booking.status === 'en_route' && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLocation(booking);
                          }}
                          sx={{ 
                            backgroundColor: '#FFD900',
                            color: 'white',
                            width: 36,
                            height: 36,
                            animation: 'blink 1.5s infinite',
                            '@keyframes blink': {
                              '0%, 50%': { opacity: 1 },
                              '51%, 100%': { opacity: 0.3 }
                            },
                            '&:hover': { backgroundColor: '#FFD900' }
                          }}
                        >
                          <LocationIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                      {isOngoingBooking(booking.status) && booking.status !== 'en_route' && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChat(booking);
                          }}
                          sx={{ 
                            backgroundColor: '#006AF9',
                            color: 'white',
                            width: 36,
                            height: 36,
                            '&:hover': { backgroundColor: '#0056b3' }
                          }}
                        >
                          <Chat sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                    
                    {/* Review buttons - for completed bookings */}
                    {booking.status === 'completed' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                        {(() => {
                          const hasReview = bookingReviews[booking.id];
                          return !hasReview;
                        })() ? (
                          // No review exists - show "Review" button
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLeaveReview(booking);
                            }}
                            disabled={reviewLoading}
                            startIcon={<RateReview sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                            sx={{ 
                              backgroundColor: '#FFB400',
                              color: '#000',
                              fontSize: { xs: '10px', sm: '12px' },
                              py: { xs: 1, sm: 1.5 },
                              px: { xs: 2, sm: 3 },
                              borderRadius: 2,
                              textTransform: 'none',
                              minWidth: 'auto',
                              fontWeight: 700,
                              height: { xs: '32px', sm: '36px' },
                              lineHeight: 1.2,
                              boxShadow: '0 2px 4px rgba(255, 180, 0, 0.3)',
                              '&:hover': {
                                backgroundColor: '#FFA000',
                                boxShadow: '0 4px 8px rgba(255, 180, 0, 0.4)',
                                transform: 'translateY(-1px)'
                              },
                              '&:active': {
                                transform: 'translateY(0)'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            Review
                          </Button>
                        ) : (
                          // Show "Edit" and "Delete" buttons if review exists
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditReview(booking);
                              }}
                              disabled={reviewLoading}
                              startIcon={<Edit sx={{ fontSize: { xs: 10, sm: 12 } }} />}
                              sx={{ 
                                borderColor: '#006AF9',
                                color: '#006AF9',
                                fontSize: { xs: '9px', sm: '11px' },
                                py: { xs: 0.8, sm: 1.2 },
                                px: { xs: 1.5, sm: 2.5 },
                                borderRadius: 2,
                                textTransform: 'none',
                                minWidth: 'auto',
                                fontWeight: 600,
                                height: { xs: '28px', sm: '32px' },
                                lineHeight: 1.2,
                                borderWidth: 2,
                                '&:hover': {
                                  borderColor: '#0056b3',
                                  backgroundColor: 'rgba(0, 106, 249, 0.08)',
                                  borderWidth: 2,
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 2px 8px rgba(0, 106, 249, 0.2)'
                                },
                                '&:active': {
                                  transform: 'translateY(0)'
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              Edit Review
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteReview(booking);
                              }}
                              disabled={reviewLoading}
                              startIcon={<Delete sx={{ fontSize: { xs: 10, sm: 12 } }} />}
                              sx={{ 
                                borderColor: '#d32f2f',
                                color: '#d32f2f',
                                fontSize: { xs: '9px', sm: '11px' },
                                py: { xs: 0.8, sm: 1.2 },
                                px: { xs: 1.5, sm: 2.5 },
                                borderRadius: 2,
                                textTransform: 'none',
                                minWidth: 'auto',
                                fontWeight: 600,
                                height: { xs: '28px', sm: '32px' },
                                lineHeight: 1.2,
                                borderWidth: 2,
                                '&:hover': {
                                  borderColor: '#b71c1c',
                                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                  borderWidth: 2,
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)'
                                },
                                '&:active': {
                                  transform: 'translateY(0)'
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              Delete Review
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Rebook button - for past bookings (completed, cancelled, no_show, etc.) */}
                    {(booking.status === 'completed' || booking.status === 'cancelled' || booking.status === 'no_show' || (!isBookingTodayOrFuture(booking.date) && booking.status !== 'pending')) && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Navigate to service provider profile
                          navigate(`/provider/${booking.providerId}`, {
                            state: { 
                              distance: booking.distance,
                              provider: booking.provider
                            }
                          });
                        }}
                        sx={{ 
                          backgroundColor: '#006AF9',
                          color: 'white',
                          fontSize: { xs: '10px', sm: '12px' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 2, sm: 3 },
                          borderRadius: 2,
                          textTransform: 'none',
                          minWidth: 'auto',
                          fontWeight: 600,
                          height: { xs: '32px', sm: '36px' },
                          lineHeight: 1.2,
                          boxShadow: '0 2px 4px rgba(0, 106, 249, 0.3)',
                          '& .MuiButton-root': {
                            minWidth: 'auto'
                          },
                          '&:hover': {
                            backgroundColor: '#0056b3',
                            boxShadow: '0 4px 8px rgba(0, 106, 249, 0.4)',
                            transform: 'translateY(-1px)'
                          },
                          '&:active': {
                            transform: 'translateY(0)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        Rebook
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>

        {filteredAndSortedBookings.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {activeTab === 'upcoming' ? 'No upcoming bookings' : 'No past bookings'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || statusFilter !== 'All' 
                ? 'Try adjusting your search or filters'
                : activeTab === 'upcoming' 
                  ? "You don't have any upcoming bookings. Start by exploring our services!"
                  : "You don't have any past bookings yet."
              }
            </Typography>
            {activeTab === 'upcoming' && (
              <Button
                variant="contained"
                onClick={() => navigate('/home')}
                sx={{ backgroundColor: '#1e3a8a' }}
              >
                Browse Services
              </Button>
            )}
          </Box>
        )}
          </Box>
        </>
      )}

      {/* Modals for listing view */}
      <RescheduleDialog />
      <CancelDialog />
      <SuccessDialog />

      {/* Debug Logs Copy Success Snackbar */}
      <Snackbar
        open={showCopySuccess}
        autoHideDuration={3000}
        onClose={() => setShowCopySuccess(false)}
        message="Debug logs copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Delete Review Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete sx={{ color: '#d32f2f', fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delete Review
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this review?
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This action cannot be undone. Your review will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            variant="outlined"
            disabled={reviewLoading}
            sx={{ 
              borderColor: '#666', 
              color: '#666',
              '&:hover': {
                borderColor: '#333',
                backgroundColor: 'rgba(102, 102, 102, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteReview}
            variant="contained"
            disabled={reviewLoading}
            startIcon={reviewLoading ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            sx={{ 
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c'
              }
            }}
          >
            {reviewLoading ? 'Deleting...' : 'Delete Review'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNav />

    </Box>
  );
};

export default Bookings; 